import React, { useEffect, useContext, useState } from "react";
import { parseISO, format } from "date-fns";
import { Link, useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../Context/AuthContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Container from "react-bootstrap/Container";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import axios from "axios";
import Breadcrumbs from "../../Navigation/Components/Breadcrumbs";
import Spinner from "react-bootstrap/Spinner";
import Image from "react-bootstrap/Image";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./EditPersonForm.css";
import { faUser } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";

function EditPersonForm() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [profile, setProfile] = useState("");
  const [birthDate, setBirthDate] = useState(null);
  const [entryDate, setEntryDate] = useState(null);
  const [role, setRole] = useState("");
  const [boss, setBoss] = useState("");
  const [inactive, setInactive] = useState(false);
  const [show, setShow] = useState(true);
  const [users, setUsers] = useState([]);
  const [profiles, setProfiles] = useState([]);
  const [profilePicture, setProfilePicture] = useState(null);
  const [existingProfilePictureUrl, setExistingProfilePictureUrl] =
    useState("");

  const [isLoading, setIsLoading] = useState(true);

  const { state } = useContext(AuthContext);

  let { pid } = useParams();

  const { t } = useTranslation();

  useEffect(() => {
    async function fetch() {
      const response = await axios({
        method: "get",
        url: `${process.env.REACT_APP_BACKEND_URL}/users/${pid}`,
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
      setFirstName(response.data.user.firstName);
      setLastName(response.data.user.lastName);
      setEmail(response.data.user.email);
      setEmployeeId(response.data.user.employeeId);
      setProfile(response.data.user.profile);
      setRole(response.data.user.role);
      if (response.data.user.boss === null) {
        setBoss("");
      } else {
        setBoss(response.data.user.boss);
      }
      setInactive(response.data.user.inactive);
      setShow(response.data.user.show_org);
      if (response.data.user.birthDate !== "") {
        setBirthDate(parseISO(response.data.user.birthDate));
      }
      if (response.data.user.entryDate !== "") {
        setEntryDate(parseISO(response.data.user.entryDate));
      }
      setExistingProfilePictureUrl(response.data.url);

      const result = await axios({
        method: "post",
        url: `${process.env.REACT_APP_BACKEND_URL}/users/form/data/${state.organization}`,
        data: {
          language: state.language,
        },
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
      setProfiles(result.data.profiles);
      setUsers(result.data.users);
    }
    fetch();
    setIsLoading(false);
  }, [state.token, pid, state.organization, state.language]);

  const navigate = useNavigate();

  const handleFirstNameChange = (e) => {
    setFirstName(e.target.value);
  };

  const handleLastNameChange = (e) => {
    setLastName(e.target.value);
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
  };

  const handlePersonIdChange = (e) => {
    setEmployeeId(e.target.value);
  };

  const handleProfileChange = (e) => {
    setProfile(e.target.value);
  };

  const handleRoleChange = (e) => {
    setRole(e.target.value);
  };

  const handleBossChange = (e) => {
    setBoss(e.target.value);
  };

  const handleInactiveChange = (e) => {
    setInactive(e.target.checked);
  };

  const handleShowChange = (e) => {
    setShow(e.target.checked);
  };

  const handleProfilePictureChange = (e) => {
    setProfilePicture({ name: e.target.name, file: e.target.files[0] });
  };

  const handleSubmit = async () => {
    try {
      const formData = new FormData();

      formData.append("firstName", firstName);
      formData.append("lastName", lastName);
      formData.append("email", email);
      formData.append("employeeId", employeeId);
      formData.append("inactive", inactive);
      formData.append("show_org", show);
      formData.append("role", role);
      formData.append("profile", profile);
      formData.append("boss", boss);
      formData.append(
        "birthDate",
        birthDate !== "" && birthDate !== null
          ? format(birthDate, "yyyy-MM-dd")
          : "",
      );
      formData.append(
        "entryDate",
        entryDate !== "" && entryDate !== null
          ? format(entryDate, "yyyy-MM-dd")
          : "",
      );

      if (profilePicture !== null) {
        formData.append(profilePicture.name, profilePicture.file);
      }

      await axios({
        method: "put",
        url: `${process.env.REACT_APP_BACKEND_URL}/users/${pid}`,
        data: formData,
        headers: {
          Authorization: `Bearer ${state.token}`,
        },
      });
      navigate("/people");
    } catch (error) {
      console.log(error);
    }
  };

  let breadcrumbs = [
    {
      title: "Personas",
      path: "/people",
    },
  ];

  if (isLoading) {
    return (
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    );
  } else {
    return (
      <Container fluid>
        <Row>
          <Breadcrumbs data={breadcrumbs} />
        </Row>
        <Row>
          <Col className="edit-person-header">
            <h2>{t("Personas.Persona.EditForm.Titulo")}</h2>
          </Col>
        </Row>
        <Row>
          <Col className="edit-person-form">
            <Form>
              <Row className="justify-content-center">
                <Col className="center mb-5 mt-5">
                  {existingProfilePictureUrl !== "" ? (
                    <Image
                      src={existingProfilePictureUrl}
                      style={{ width: 200 }}
                      roundedCircle
                      fluid
                    />
                  ) : (
                    <FontAwesomeIcon
                      icon={faUser}
                      size="10x"
                      className="avatar"
                    />
                  )}
                </Col>
              </Row>
              <Row>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.Nombres")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    onChange={handleFirstNameChange}
                    value={firstName}
                  />
                </Col>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.Apellidos")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    onChange={handleLastNameChange}
                    value={lastName}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.Email")}
                  </Form.Label>
                  <Form.Control
                    type="email"
                    onChange={handleEmailChange}
                    value={email}
                  />
                </Col>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.IdEmpleado")}
                  </Form.Label>
                  <Form.Control
                    type="text"
                    onChange={handlePersonIdChange}
                    value={employeeId}
                  />
                </Col>
              </Row>
              <Row>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.Perfil")}
                  </Form.Label>
                  <Form.Select
                    aria-label="Perfil"
                    onChange={handleProfileChange}
                    value={profile}
                  >
                    <option value={null}> </option>
                    {profiles.map((item) => (
                      <option key={item._id} value={item._id}>
                        {state.language === "es"
                          ? item.name
                          : item.name_english}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                <Col className="mb-3">
                  <Form.Label>
                    {t("Personas.Persona.EditForm.RolUsuario")}
                  </Form.Label>
                  <Form.Select
                    aria-label="Rol"
                    onChange={handleRoleChange}
                    value={role}
                  >
                    <option value=""> </option>
                    <option value="user">
                      {t("Personas.Persona.EditForm.Usuario")}
                    </option>
                    <option value="admin">
                      {t("Personas.Persona.EditForm.Administrador")}
                    </option>
                    {state.role === "root" || state.role === "owner" ? (
                      <option value="owner">
                        {t("Personas.Persona.EditForm.Owner")}
                      </option>
                    ) : (
                      <></>
                    )}
                  </Form.Select>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t("Personas.Persona.EditForm.JefeDirecto")}
                    </Form.Label>
                    <Form.Select
                      aria-label="Default select example"
                      onChange={handleBossChange}
                      value={boss}
                    >
                      <option value=""> </option>
                      {users.map((item) => (
                        <option key={item._id} value={item._id}>
                          {item.firstName} {item.lastName}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>Imagen de Perfil</Form.Label>
                    <Form.Control
                      type="file"
                      name="profilePicture"
                      onChange={(e) => handleProfilePictureChange(e)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="align-items-center">
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t("Personas.Persona.EditForm.FechaNacimiento")}
                    </Form.Label>
                    <DatePicker
                      showIcon
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      selected={birthDate}
                      onChange={(date) => setBirthDate(date)}
                    />
                  </Form.Group>
                </Col>
                <Col>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      {t("Personas.Persona.EditForm.FechaIngreso")}
                    </Form.Label>
                    <DatePicker
                      showIcon
                      peekNextMonth
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      selected={entryDate}
                      onChange={(date) => setEntryDate(date)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="justify-content-center">
                <Col>
                  <Form.Check
                    inline
                    label={t("Personas.Persona.EditForm.Inactivo")}
                    name="group1"
                    type="checkbox"
                    onChange={handleInactiveChange}
                    checked={inactive}
                  />
                  <Form.Check
                    inline
                    label={t("Personas.Persona.EditForm.MostrarOrganigrama")}
                    name="group2"
                    type="checkbox"
                    onChange={handleShowChange}
                    checked={show}
                  />
                </Col>
                <Col></Col>
              </Row>
              <Row>
                <Col>
                  <Button className="primary" onClick={handleSubmit}>
                    {t("Personas.Persona.EditForm.Guardar")}
                  </Button>
                  <Link to="/people">
                    <Button className="danger">
                      {t("Personas.Persona.EditForm.Cancelar")}
                    </Button>
                  </Link>
                </Col>
              </Row>
            </Form>
          </Col>
        </Row>
      </Container>
    );
  }
}

export default EditPersonForm;
